import { FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box } from '@mui/material';
import { GridList } from '../common/grid/GridList';
import { QcsTableCell } from '../common/basic/QcsTableCell';
import { QcsTypography } from '../common/basic/QcsTypography';
import { QcsTooltip } from '../common/basic/QcsTooltip';
import { QcsButton } from '../common/basic/QcsButton';
import { ShowError } from '../ShowError';
import { IntegrationAddKeyModal } from './IntegrationAddKeyModal';
import { IntegrationKeyExpiry } from './IntegrationKeyExpiry';
import { getUnrevokedKeys, getWorkingKeys, IntegrationCredentials, isExpired, MAX_ACTIVE_KEYS } from './integrationUtils';
import { formatDate } from '../../utils/date';
import { apiClientService } from '@api/services/services';
import { resources } from '@api/resources';
import { ApiClientDetailDto } from '@api/models/apiClient/ApiClientDetailDto';
import { ApiClientKeyDto } from '@api/models/apiClient/ApiClientKeyDto';

interface Props {
  detail: ApiClientDetailDto;
  onChanged: () => void;
  onCredentialsIssued: (credentials: IntegrationCredentials) => void;
}

export const IntegrationKeys: FC<Props> = ({ detail, onChanged, onCredentialsIssued }) => {
  const { t, i18n } = useTranslation();
  const [adding, setAdding] = useState(false);

  const unrevokedKeys = getUnrevokedKeys(detail.keys);
  const canAddKey = unrevokedKeys.length < MAX_ACTIVE_KEYS;

  const handleRevoke = async (key: ApiClientKeyDto) => {
    //Revoking the last key that still works stops the API access, even if expired keys are left.
    const workingKeys = getWorkingKeys(detail.keys);
    const isLastKey = workingKeys.length === 1 && workingKeys[0].id === key.id;
    if (await apiClientService.revokeKey(detail.id, key.id, isLastKey)) onChanged();
  };

  const handleKeyAdded = (credentials: IntegrationCredentials) => {
    setAdding(false);
    onCredentialsIssued(credentials);
    onChanged();
  };

  const getKeyState = (key: ApiClientKeyDto) => {
    if (key.revokedAt) return t('integration.apiAccess.keys.revokedAt', { date: formatDate(i18n, key.revokedAt, true) });
    if (isExpired(key.expiresAt)) return t('integration.apiAccess.keys.expired');
    return t('integration.apiAccess.keys.active');
  };

  const renderKey = (key: ApiClientKeyDto) => (
    <>
      <QcsTableCell>{key.kid}</QcsTableCell>
      <QcsTableCell>{getKeyState(key)}</QcsTableCell>
      <QcsTableCell hideOnMobile>{formatDate(i18n, key.createdAt, true)}</QcsTableCell>
      <QcsTableCell hideOnMobile>
        <IntegrationKeyExpiry expiresAt={key.expiresAt} revoked={!!key.revokedAt} />
      </QcsTableCell>
      <QcsTableCell hideOnMobile>{key.lastUsedAt ? formatDate(i18n, key.lastUsedAt, true) : t('integration.apiAccess.never')}</QcsTableCell>
      <QcsTableCell align="right">
        {!key.revokedAt && !isExpired(key.expiresAt) && (
          <QcsButton variant="outlined" color="error" onClick={() => handleRevoke(key)}>
            {t('integration.apiAccess.keys.revoke')}
          </QcsButton>
        )}
      </QcsTableCell>
    </>
  );

  return (
    <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: '1rem' }}>
      {adding && <IntegrationAddKeyModal detail={detail} onClose={() => setAdding(false)} onAdded={handleKeyAdded} />}

      <QcsTypography variant="body2">{t('integration.apiAccess.keys.rotationHint')}</QcsTypography>
      <Box>
        <QcsTooltip title={canAddKey ? undefined : t('errorCodes.280', { max: MAX_ACTIVE_KEYS })}>
          <span>
            <QcsButton variant="contained" onClick={() => setAdding(true)} disabled={!canAddKey}>
              {t('integration.apiAccess.keys.add')}
            </QcsButton>
          </span>
        </QcsTooltip>
      </Box>
      <ShowError resource={[resources.revokeApiClientKey, resources.revokeLastApiClientKey]} />

      <GridList<ApiClientKeyDto>
        headers={[
          { captionStr: 'integration.apiAccess.keys.kid' },
          { captionStr: 'integration.apiAccess.keys.state' },
          { captionStr: 'integration.apiAccess.keys.createdAt', hideOnMobile: true },
          { captionStr: 'integration.apiAccess.keys.expiresAt', hideOnMobile: true },
          { captionStr: 'integration.apiAccess.lastUsedAt', hideOnMobile: true },
          {},
        ]}
        data={detail.keys}
        renderData={renderKey}
        isRowDisabled={(key) => !!key.revokedAt}
        search={false}
        hidePagination
      />
    </Box>
  );
};
